import { useMemo } from "react"
import { capitalize, map, chain, keys, find, keyBy, get } from "lodash"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Paper } from "@mui/material"
import { ROLE, PERMISSION_LEVEL } from "helpers/shapes"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS, hasPermission } from "helpers/permissions"
import { formatLongDate } from "helpers/formatters/dates"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"
import { MEMBER } from "./shapes"

export default function ResourceMembers({ account, resourceId, resourceType, permissionLevel, role, resourceMembers, accountMembers, update, isOpPro, tabs }) {
  const hasWriteAccess = role === "admin" || hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)
  const nonEditable = isOpPro

  const membersById = useMemo(() => keyBy(accountMembers, "id"), [accountMembers])

  const availableOptionLabels = useMemo(() => chain(membersById)
    .omitBy((_, memberId) => find(resourceMembers, m => m.id === memberId))
    .mapValues(m => m.name)
    .value(),
  [membersById, resourceMembers])

  const addForm = useMemo(() => {
    if (!hasWriteAccess || nonEditable) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        ${map(data.members, (memberId, index) => `member${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { memberId: "${memberId}", permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, resourceId, resourceType }

      update(query, variables, account, {
        reloadData: "resourceMembers",
      }, cb)
    }

    return {
      title: "Add Members",
      submitText: "Add Member",
      track: {
        type: "resource-permissions-add-member",
        payload: ({ members, permissionLevel }) => ({
          members: map(members, id => get(membersById, [id, "name"], id)),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "members",
        label: "Members",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, nonEditable, availableOptionLabels, resourceId, resourceType, update, account, membersById])

  const editForm = useMemo(() => {
    if (nonEditable) { return null }

    function onEdit(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { memberId: "${data.id}", permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId,
        resourceType,
        permissionLevel: data.permissionLevel,
      }

      update(query, variables, account, {
        reloadData: "resourceMembers",
      }, cb)
    }

    return {
      title: "Edit Permission Level",
      submitText: "Save",
      track: {
        type: "resource-permissions-edit-member",
        payload: ({ id, permissionLevel }) => ({
          resource: get(membersById, [id, "name"], id),
          permissionLevel,
        }),
      },
      isEnabled: () => hasWriteAccess,
      onSubmit: onEdit,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [resourceId, resourceType, update, account, hasWriteAccess, membersById, nonEditable])

  const deleteForm = useMemo(() => {
    if (nonEditable) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!) {
        ${map(data, (member, index) => `member${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { memberId: "${member.id}", permissionLevel: NONE }) { id }`)}
      }`

      const variables = { resourceId, resourceType }

      update(query, variables, account, {
        reloadData: "resourceMembers",
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "resource-permissions-remove-member",
        payload: ({ name }) => ({ resource: name }),
      },
      isEnabled: () => hasWriteAccess,
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, resourceId, resourceType, update, nonEditable])

  return <div className={classNames("modules_resource-members", "with-header")}>
    <UpsellTabs id="resource-members" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Resource Permissions"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={resourceMembers}
        columns={[{
          name: "Name",
          id: "name",
        }, {
          name: "Email",
          id: "email",
        }, {
          name: "Created",
          id: "createdAt",
          formatter: formatLongDate,
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

ResourceMembers.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  permissionLevel: PERMISSION_LEVEL,
  resourceId: PropTypes.string,
  resourceAlias: PropTypes.string.isRequired,
  resourceType: PropTypes.string,
  resourceMembers: PropTypes.arrayOf(MEMBER).isRequired,
  accountMembers: PropTypes.arrayOf(MEMBER).isRequired,
  update: PropTypes.func.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
